/**
 * useParecerQueries.ts
 *
 * Composable to fetch and derive Parecer status/query data from the API.
 */

import type { DecisionOutcome } from './useKycDecisionDraft';
import parecerApi, {
  PARECER_QUEUE_STAGE_SLUG,
  PARECER_START_STAGE_SLUG,
  type AvailableAction,
  type Parecer,
} from '@/api/modules/parecerApi';
import type { EntityType } from '@/types/kyc';
import {
  buildEntityParecerStatus,
  getActiveParecerLane,
  getCurrentParecerDecision,
  isExpiredDecision,
  type EntityParecerStatus,
  type ParecerHydratedRecord,
} from './parecerStatus';

// --- Type Definitions ---
export type { EntityType } from '@/types/kyc';

export type ParecerState = 'none' | 'in_progress' | 'approved' | 'rejected' | 'expired';

interface LatestActiveDecisionSelection {
  requestId: string;
  decision: DecisionOutcome;
}

const toTimestamp = (dateValue?: string | null) => {
  if (!dateValue) return 0;
  const parsed = new Date(dateValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

// --- Helper Functions ---

/**
 * Pull each parecer's stage manifest, plus — for one sitting in the unassigned queue — the
 * outbound transition that takes it. `GET /actions` already returns `is_disabled` and a
 * pt-BR `reason` per transition, which is exactly the button state and tooltip the card
 * needs, so there is nothing to recompute on the client.
 */
const attachStageManifestsAndTakeTransitions = async (
  parecers: Parecer[],
): Promise<ParecerHydratedRecord[]> => {
  // Load each distinct stage's UI manifest once; null when the manifest fails to load.
  const stageSlugs = [...new Set(parecers.map((parecer) => parecer.current_stage?.slug).filter(Boolean))];
  const manifestCache = new Map<string, Awaited<ReturnType<typeof parecerApi.getUiManifest>> | null>();

  const queuedParecers = parecers.filter(
    (parecer) => parecer.current_stage?.slug === PARECER_QUEUE_STAGE_SLUG,
  );
  const takeTransitions = new Map<string, AvailableAction | null>();

  await Promise.all([
    ...stageSlugs.map(async (stageSlug) => {
      try {
        manifestCache.set(stageSlug!, await parecerApi.getUiManifest(stageSlug));
      } catch {
        manifestCache.set(stageSlug!, null);
      }
    }),
    ...queuedParecers.map(async (parecer) => {
      try {
        const outbound = await parecerApi.getAvailableActions(parecer.id);
        takeTransitions.set(
          parecer.id,
          outbound.find((t) => t.to_stage_slug === PARECER_START_STAGE_SLUG) ?? null,
        );
      } catch {
        takeTransitions.set(parecer.id, null);
      }
    }),
  ]);

  return parecers.map((parecer) => ({
    parecer,
    uiManifest: manifestCache.get(parecer.current_stage.slug) ?? null,
    takeTransition: takeTransitions.get(parecer.id) ?? null,
  }));
};


const deriveStateFromOutcome = (
  decision: DecisionOutcome,
  expirationDate: string | null,
  stageSlug?: string | null,
): ParecerState => {
  if (stageSlug && stageSlug !== 'finalized') {
    return 'in_progress';
  }

  if (isExpiredDecision(decision, expirationDate)) {
    return 'expired';
  }

  switch (decision) {
    case 'draft':
    case 'submitted':
    case 'rfi':
    case 'escalated':
      return 'in_progress';
    case 'approved':
    case 'approved_with_conditions':
      return 'approved';
    case 'rejected':
      return 'rejected';
    default:
      return 'none';
  }
};

const compareParecerNewestFirst = (left: Parecer, right: Parecer) =>
  toTimestamp(right.updated_at || right.inserted_at) - toTimestamp(left.updated_at || left.inserted_at);

const getDecisionStateForRecord = (
  parecer: Parecer,
  activeLane: ReturnType<typeof getActiveParecerLane>,
) => {
  const stageSlug = parecer.current_stage?.slug;
  const finalDecision = parecer.final_decision;
  const proposalDecision = parecer.decisions?.proposal;

  const selectedDecision = (() => {
    if (stageSlug === 'finalized') return finalDecision ?? null;
    if (stageSlug === 'manager_review') return proposalDecision ?? null;
    if (activeLane === 'final') return finalDecision ?? proposalDecision ?? null;
    return proposalDecision ?? finalDecision ?? null;
  })();

  return {
    stageSlug,
    decision: selectedDecision?.decision ?? null,
    expirationDate: selectedDecision?.expiration_date ?? null,
  };
};

// --- Composable ---

export function useParecerQueries() {
  const emptyEntityParecerStatus = (): EntityParecerStatus => ({
    currentParecer: null,
    latestFinalizedParecer: null,
    hasAnyParecer: false,
  });

  const getHydratedParecersForEntity = async (
    entityType: EntityType,
    entityId: string,
    requestId?: string | null,
  ): Promise<ParecerHydratedRecord[]> => {
    // List endpoint returns lightweight summaries only.
    const response = requestId
      ? await parecerApi.listParecers({ report_request_id: requestId, page_size: 100 })
      : await parecerApi.getParecersForEntity(entityId, entityType);
    const filteredItems = response.items;

    if (filteredItems.length === 0) return [];

    // Fetch the full parecer for each summary; ignore individual failures.
    const parecerResults = await Promise.allSettled(
      filteredItems.map((item) => parecerApi.getParecer(item.id)),
    );

    const parecers = parecerResults
      .flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
      .sort(compareParecerNewestFirst);

    if (!parecers.length) return [];

    return attachStageManifestsAndTakeTransitions(parecers);
  };

  const getHydratedParecersForReportRequests = async (
    requestIds: string[],
  ): Promise<ParecerHydratedRecord[]> => {
    const uniqueRequestIds = [...new Set(requestIds)].filter(Boolean);
    if (!uniqueRequestIds.length) return [];

    const listResults = await Promise.allSettled(
      uniqueRequestIds.map((requestId) =>
        parecerApi.listParecers({ report_request_id: requestId, page_size: 100 })
      ),
    );

    const items = listResults.flatMap((result) =>
      result.status === 'fulfilled' ? result.value.items : []
    );
    if (!items.length) return [];

    const parecerResults = await Promise.allSettled(
      items.map((item) => parecerApi.getParecer(item.id)),
    );

    const parecers = parecerResults
      .flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
      .sort(compareParecerNewestFirst);

    if (!parecers.length) return [];

    return attachStageManifestsAndTakeTransitions(parecers);
  };

  const getEntityParecerStatus = async (
    entityType: EntityType,
    entityId: string,
    preferredRequestId?: string | null,
  ): Promise<EntityParecerStatus> => {
    if (preferredRequestId) {
      const preferredRecords = await getHydratedParecersForEntity(entityType, entityId, preferredRequestId);
      if (!preferredRecords.length) return emptyEntityParecerStatus();
      return buildEntityParecerStatus(preferredRecords, preferredRequestId);
    }

    const records = await getHydratedParecersForEntity(entityType, entityId);
    return buildEntityParecerStatus(records);
  };

  const getLatestActiveDecisionForEntity = async (
    entityType: EntityType,
    entityId: string,
  ): Promise<LatestActiveDecisionSelection | null> => {
    const status = await getEntityParecerStatus(entityType, entityId);
    const currentDecision = getCurrentParecerDecision(status.currentParecer);
    return currentDecision
      ? {
          requestId: currentDecision.requestId,
          decision: currentDecision.decision,
        }
      : null;
  };

  const getReportParecerStateMap = async (
    entityType: EntityType,
    entityId: string,
  ): Promise<Map<string, { state: ParecerState; stageSlug?: string }>> => {
    const records = await getHydratedParecersForEntity(entityType, entityId);
    const stateMap = new Map<string, { state: ParecerState; stageSlug?: string }>();

    records.forEach(({ parecer, uiManifest }) => {
      if (stateMap.has(parecer.report_request_id)) return;

      const activeLane = getActiveParecerLane(uiManifest);
      const { stageSlug, decision, expirationDate } = getDecisionStateForRecord(parecer, activeLane);

      const state = decision
        ? deriveStateFromOutcome(decision, expirationDate, stageSlug)
        : stageSlug && stageSlug !== 'finalized'
          ? 'in_progress'
          : 'none';

      stateMap.set(parecer.report_request_id, {
        state,
        stageSlug,
      });
    });

    return stateMap;
  };

  const getReportParecerStateMapForRequestIds = async (
    requestIds: string[],
  ): Promise<Map<string, { state: ParecerState; stageSlug?: string }>> => {
    const records = await getHydratedParecersForReportRequests(requestIds);
    const stateMap = new Map<string, { state: ParecerState; stageSlug?: string }>();

    records.forEach(({ parecer, uiManifest }) => {
      if (stateMap.has(parecer.report_request_id)) return;

      const activeLane = getActiveParecerLane(uiManifest);
      const { stageSlug, decision, expirationDate } = getDecisionStateForRecord(parecer, activeLane);

      const state = decision
        ? deriveStateFromOutcome(decision, expirationDate, stageSlug)
        : stageSlug && stageSlug !== 'finalized'
          ? 'in_progress'
          : 'none';

      stateMap.set(parecer.report_request_id, {
        state,
        stageSlug,
      });
    });

    return stateMap;
  };

  return {
    getHydratedParecersForEntity,
    getEntityParecerStatus,
    getReportParecerStateMap,
    getReportParecerStateMapForRequestIds,
    getLatestActiveDecisionForEntity,
  };
}
